import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/**
 * Shape of the per-entity tab-visibility config returned by the backend.
 */
export interface EntityTabConfig {
  /** Tab titles hidden from non-owners. */
  ownerHiddenTabs: string[];
  /** For private entities: tabs intentionally revealed to non-owners. */
  ownerVisibleTabs: string[];
  /** External security finding lock that blocks owner reveals. */
  securityFindingPrivateLock?: boolean;
  /** Optional reason for the active security finding lock. */
  securityFindingPrivateLockReason?: string;
  /** User entity ref of who last updated the config. */
  updatedBy?: string;
  /** ISO timestamp of the last update. */
  updatedAt?: string;
}

/**
 * Client API for the shared, per-entity tab-visibility backend.
 */
export interface EntityTabConfigApi {
  /** Read the shared config for an entity (available to any signed-in user). */
  getConfig(entityRef: string): Promise<EntityTabConfig>;
  /** Update only hidden tabs for an entity (owners only, server-side atomic). */
  setOwnerHiddenTabs(
    entityRef: string,
    ownerHiddenTabs: string[],
  ): Promise<EntityTabConfig>;
  /** Update only revealed tabs for private entities (owners only, atomic). */
  setOwnerVisibleTabs(
    entityRef: string,
    ownerVisibleTabs: string[],
  ): Promise<EntityTabConfig>;
}

export const entityTabConfigApiRef = createApiRef<EntityTabConfigApi>({
  id: 'plugin.entity-tab-config.service',
});

/**
 * Default implementation that talks to the entity-tab-config backend plugin.
 */
export class EntityTabConfigClient implements EntityTabConfigApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('entity-tab-config');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message =
        typeof body.error === 'string'
          ? body.error
          : `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(message);
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return response.json();
  }

  async getConfig(entityRef: string): Promise<EntityTabConfig> {
    const query = `?entityRef=${encodeURIComponent(entityRef)}`;
    return this.request<EntityTabConfig>(`/config${query}`);
  }

  async setOwnerHiddenTabs(
    entityRef: string,
    ownerHiddenTabs: string[],
  ): Promise<EntityTabConfig> {
    return this.request<EntityTabConfig>('/config/owner-hidden-tabs', {
      method: 'PATCH',
      body: JSON.stringify({
        entityRef,
        ownerHiddenTabs,
      }),
    });
  }

  async setOwnerVisibleTabs(
    entityRef: string,
    ownerVisibleTabs: string[],
  ): Promise<EntityTabConfig> {
    return this.request<EntityTabConfig>('/config/owner-visible-tabs', {
      method: 'PATCH',
      body: JSON.stringify({
        entityRef,
        ownerVisibleTabs,
      }),
    });
  }
}
